import React, { useEffect, useRef, useState } from "react";
import Highcharts from "highcharts";
import { Box, FormControlLabel, Switch, useTheme } from "@mui/material";
import HighchartsReact from "highcharts-react-official";
import { useTranslation } from "react-i18next";
import { NoAccountsStub, WithLoadingSpinner } from "@components";
import { InvestmentChartDataItem } from "@hooks";
import { ChartWrapper } from "../../common";
import { useHighchartsOptions } from "./useChartOptions";

type Props = {
  chartData: {
    totalValues: InvestmentChartDataItem[];
    investedCapitalValues: InvestmentChartDataItem[];
    comparisonValues: InvestmentChartDataItem[];
  };
  isFetching?: boolean;
  isFetched?: boolean;
  showInvestedCapitalToggle?: boolean;
  seriesNameMap?: {
    totalValues: string;
    investedCapitalValues?: string;
    comparisonValues?: string;
  };
  colors?: {
    totalValues: string;
    investedCapitalValues?: string;
    comparisonValues?: string;
  };
  tooltipType?: "value" | "percentage";
  legendCircleSize?: number;
  height?: number;
  labelStyle?: {
    fontSize?: string;
    lineHeight?: string;
    fontWeight?: string;
    textTransform?: "none" | "uppercase";
  };
};

export const ValuationChart = ({
  chartData,
  isFetching,
  isFetched,
  showInvestedCapitalToggle = true,
  seriesNameMap,
  colors,
  tooltipType = "value",
  legendCircleSize = 8,
  height,
  labelStyle,
}: Props) => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [withInvestedCapital, setWithInvestedCapital] = useState(false);
  const { t } = useTranslation();
  const theme = useTheme();

  const chartOptions = useHighchartsOptions({
    chartData,
    theme,
    withInvestedCapital,
    seriesNameMap,
    colors,
    tooltipType,
    legendCircleSize,
    labelStyle,
    height,
  });

  useEffect(() => {
    chartComponentRef.current?.chart?.reflow();
  }, [chartOptions]);

  return (
    <Box>
      <WithLoadingSpinner isLoading={Boolean(!isFetched || isFetching)} mb={1}>
        {isFetched && !isFetching && !chartData.totalValues.length ? (
          <NoAccountsStub height="330px" />
        ) : (
          <ChartWrapper>
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              ref={chartComponentRef}
            />
          </ChartWrapper>
        )}
      </WithLoadingSpinner>
      {showInvestedCapitalToggle && (
        <Box>
          <Box mb={3}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  size="medium"
                  checked={withInvestedCapital}
                  onChange={() => setWithInvestedCapital(!withInvestedCapital)}
                />
              }
              label={t(
                "dashboardPage.dashboard.performance.viewInvestedCapital"
              )}
              sx={{
                marginLeft: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: theme.typography.body1,
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
